import { OPTIMISTIC_ID_PREFIX } from '../utils/chatHelpers';
import { DELTA_ID_PREFIX } from './types';
export function reduceMessages(state, action) {
  switch (action.type) {
    case 'MESSAGE_APPEND':
      {
        if (state.messages.some(msg => msg.id === action.message.id)) return state;
        const deltaIds = [];
        const base = [];
        for (const msg of state.messages) {
          if (msg.id.startsWith(DELTA_ID_PREFIX)) {
            deltaIds.push(msg.id);
          } else {
            base.push(msg);
          }
        }
        const hasDelta = deltaIds.length > 0;
        if (!hasDelta && state.turnId === null) {
          return Object.assign({}, state, {
            messages: [...state.messages, action.message]
          });
        }
        const appendMap = new Map(state.messageTurnIdMap);
        if (action.keepDeltas) {
          // Cancellation interrupt: keep the partial streamed text visible and
          // append after it. The next TURN_STARTED sweeps the stale deltas.
          if (state.turnId !== null) {
            appendMap.set(action.message.id, state.turnId);
          }
          return Object.assign({}, state, {
            messages: [...state.messages, action.message],
            messageTurnIdMap: appendMap
          });
        }
        for (const deltaId of deltaIds) {
          appendMap.delete(deltaId);
        }
        if (state.turnId !== null) {
          appendMap.set(action.message.id, state.turnId);
        }
        return Object.assign({}, state, {
          messages: [...(hasDelta ? base : state.messages), action.message],
          messageTurnIdMap: appendMap
        });
      }
    case 'TURN_STARTED':
      {
        // Sweep deltas left over from a cancelled turn before the next one streams.
        const cleaned = state.messages.filter(msg => !msg.id.startsWith(DELTA_ID_PREFIX));
        if (cleaned.length === state.messages.length) return state;
        const cleanedMap = new Map(state.messageTurnIdMap);
        for (const msg of state.messages) {
          if (msg.id.startsWith(DELTA_ID_PREFIX)) cleanedMap.delete(msg.id);
        }
        return Object.assign({}, state, {
          messages: cleaned,
          messageTurnIdMap: cleanedMap
        });
      }
    case 'MESSAGE_DELTA_UPDATE':
      {
        const existingIndex = state.messages.findIndex(msg => msg.id === action.message.id);
        const needsMapUpdate = state.turnId !== null && state.messageTurnIdMap.get(action.message.id) !== state.turnId;
        const deltaMap = needsMapUpdate ? new Map(state.messageTurnIdMap) : state.messageTurnIdMap;
        if (needsMapUpdate) {
          deltaMap.set(action.message.id, state.turnId);
        }
        if (existingIndex >= 0) {
          const updated = [...state.messages];
          updated[existingIndex] = action.message;
          return Object.assign({}, state, {
            messages: updated,
            messageTurnIdMap: deltaMap
          });
        }
        return Object.assign({}, state, {
          messages: [...state.messages, action.message],
          messageTurnIdMap: deltaMap
        });
      }
    case 'MESSAGE_REPLACE_OPTIMISTIC':
      {
        const optimistic = state.messages[state.messages.length - 1];
        const replaceMap = new Map(state.messageTurnIdMap);
        if (optimistic) {
          replaceMap.delete(optimistic.id);
        }
        if (state.turnId !== null) {
          replaceMap.set(action.message.id, state.turnId);
        }
        return Object.assign({}, state, {
          messages: [...state.messages.slice(0, -1), action.message],
          messageTurnIdMap: replaceMap
        });
      }
    case 'MESSAGE_REPLACE_ALL':
      {
        const kept = state.messages.filter(m => !m.id.startsWith(OPTIMISTIC_ID_PREFIX));
        const existingIds = new Set(kept.map(m => m.id));
        const newOnly = action.messages.filter(m => !existingIds.has(m.id));
        if (state.turnId === null || newOnly.length === 0) {
          return Object.assign({}, state, {
            messages: [...kept, ...newOnly]
          });
        }
        const replaceAllMap = new Map(state.messageTurnIdMap);
        for (const msg of newOnly) {
          replaceAllMap.set(msg.id, state.turnId);
        }
        return Object.assign({}, state, {
          messages: [...kept, ...newOnly],
          messageTurnIdMap: replaceAllMap
        });
      }
    case 'MESSAGE_REMOVE_LAST_OPTIMISTIC':
      {
        var _lastAfterDelta$id;
        const hasDelta = state.messages.some(msg => msg.id.startsWith(DELTA_ID_PREFIX));
        const cleanedMap = new Map(state.messageTurnIdMap);
        const cleaned = [];
        for (const msg of state.messages) {
          if (msg.id.startsWith(DELTA_ID_PREFIX)) {
            cleanedMap.delete(msg.id);
          } else {
            cleaned.push(msg);
          }
        }
        const lastAfterDelta = cleaned[cleaned.length - 1];
        const hasOptimistic = lastAfterDelta === null || lastAfterDelta === void 0 || (_lastAfterDelta$id = lastAfterDelta.id) === null || _lastAfterDelta$id === void 0 ? void 0 : _lastAfterDelta$id.startsWith(OPTIMISTIC_ID_PREFIX);
        if (!hasOptimistic && !hasDelta) return state;
        if (hasOptimistic) {
          cleanedMap.delete(lastAfterDelta.id);
          cleaned.pop();
        }
        return Object.assign({}, state, {
          messages: cleaned,
          messageTurnIdMap: cleanedMap
        });
      }
    case 'RESET':
      return Object.assign({}, state, {
        messages: [],
        messageTurnIdMap: new Map()
      });
    default:
      return state;
  }
}